import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import { FeedContext } from 'js/social/contexts';
import UserCard, { User } from 'js/social/UserCard';
import { ResourcePayload, Tag } from 'js/social/shared/types';
import {
  AttachmentStatus,
  useAttachment,
} from 'js/social/shared/attachments/useAttachment';
import { usePaperLinkDetection } from 'js/social/shared/attachments/usePaperLinkDetection';
import { CreateSocialItemModal } from './CreateSocialItemModal';
import { useDraft } from './useDraft';
import { ImpressionProps } from 'js/utils/impressions';

type Props = {
  /**
   * Some form of identifier for the social item.
   * Used for saving drafts in sessionStorage.
   */
  id: string;
  /**
   * Info about the current user, used to show their
   * name, image, and affiliation.
   */
  author: User;
  /** Copy for the text area input */
  placeholder?: string;
  /** Copy for the primary CTA button. */
  submitButtonText: string;
  /** Anything to prefill the input box with */
  initialContent?: string;
  /** Anything to prefill the tags with */
  initialTags?: Tag[];
  /** Allow attaching papers or images */
  allowAttachments: boolean;
  /** If this is a reply, provide the contents of the post reply to */
  replyTo?: {
    node: ReactNode;
    profileImg: ReactNode;
  };
  /**
   * Clicking outside the modal, clicking Cancel,
   * or clicking the close 'X' calls this function.
   */
  onClose(): void;
  /** This function is called when a user submits their social item. */
  onSubmit(payload: ResourcePayload): void;
  impressionPropsGen: (clickTarget?: string) => ImpressionProps;
};

export const CreateSocialItem = ({
  id,
  author,
  placeholder,
  submitButtonText,
  initialContent,
  initialTags,
  allowAttachments = false,
  replyTo,
  onClose,
  onSubmit,
  impressionPropsGen,
}: Props) => {
  const {
    userActions: { getUserData },
  } = useContext(FeedContext);

  const { draftContent, draftTags, saveDraft, clearDraft } = useDraft(
    id,
    initialContent,
    initialTags,
    getUserData,
  );

  const modalRef = useRef<HTMLDialogElement>(null);

  const [content, setContent] = useState(draftContent);
  const [tags, setTags] = useState<Tag[]>(draftTags);
  const [submitting, setSubmitting] = useState(false);
  const [prompt, setPrompt] = useState(false);

  // Attachments
  const {
    image,
    paper,
    content: attachmentContent,
    status: attachmentStatus,
    hasAttachment,
    onPaperLinksChange,
  } = useAttachment();
  usePaperLinkDetection(
    content,
    allowAttachments ? onPaperLinksChange : () => null,
  );

  // Cancel prompt if the user removes the attachment
  useEffect(() => {
    if (prompt && !hasAttachment) {
      setPrompt(false);
    }
  }, [prompt, hasAttachment]);

  const hasText = !!content.trim() || tags.length > 0;
  const hasContent = hasText || hasAttachment;
  const loading = attachmentStatus === AttachmentStatus.Loading || submitting;
  const allowSubmit = hasContent && !loading;

  const submit = async () => {
    if (!allowSubmit) return;

    if (!prompt && !hasText && hasAttachment) {
      return setPrompt(true);
    }

    setSubmitting(true);
    try {
      await onSubmit({ content, tags, image, paper });
      clearDraft();
      onClose();
    } catch {
      setSubmitting(false);
    }
  };

  const closeModal = useCallback(() => {
    if (!loading && initialContent === undefined) {
      saveDraft(content, tags);
    }
    onClose();
  }, [loading, onClose, initialContent, saveDraft, content, tags]);

  return (
    <CreateSocialItemModal
      ref={modalRef}
      replyTo={replyTo}
      submitButtonText={submitButtonText}
      submitButtonEnabled={allowSubmit}
      currentUserProfileImgUrl={author.photo}
      currentUserProfileNode={<UserCard {...author} />}
      placeholder={placeholder}
      content={content}
      tags={tags}
      image={image}
      paper={paper}
      allowAttachments={allowAttachments}
      onClose={closeModal}
      onSubmit={submit}
      onChange={(content, tags) => {
        setContent(content || '');
        setTags(tags);
      }}
      contextNode={attachmentContent}
      impressionPropsGen={impressionPropsGen}
    />
  );
};
